var CookieConsent = (function () {
  'use strict';

  const DEFAULTS = {
    type: 'checkbox',
    prefix: 'cookie-consent',
    append: true,
    appendDelay: 500,
    acceptAllButton: false,
    labels: {
      title: 'Cookies & Privacy',
      description: '<p>This site makes use of third-party cookies. Read more in our <a href="/privacy-policy">privacy policy</a>.</p>',
      button: {
        default: 'Save preferences',
        acceptAll: 'Accept all',
      },
      aria: {
        button: 'Confirm cookie settings',
        tabList: 'List with cookie types',
        tabToggle: 'Toggle cookie tab',
      },
    },
  };

  /**
   * Test is LocalStorage is supported.
   */
  const supportsLocalStorage = () => {
    const test = 'localstorage-test-key';
    try {
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  };

  /**
   * Get nested entry from an object by dot string notation: `config.get('foo.bar')`.
   */
  const getEntryByDotString = (object, entryString) => {
    const entries = entryString.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '').split('.');
    return entries.reduce((acc, entry) => {
      if (acc && entry in acc) {
        return acc[entry];
      }
      return undefined;
    }, object);
  };

  /**
   * Config getter with defaults fallback and warning when required values are missing.
   */
  const Config = settings => {
    return {
      get: (entryString, required = false) => {
        const value = getEntryByDotString(settings, entryString);
        if (!value && required) {
          console.warn(`Required setting '${entryString}' is missing.`);
          return undefined;
        }
        return value === undefined ? getEntryByDotString(DEFAULTS, entryString) : value;
      },
    };
  };

  /**
   * Composer of functions. Will accept 0 or more functions.
   */
  var compose = (...fns) => arg =>
    fns.reduceRight(
      (acc, fn) => fn(acc),
      arg
    );

  /**
   * Check if argument is defined.
   */
  const defined = x => typeof x !== 'undefined';

  /**
   * Convert HTML string to DOM element.
   */
  const htmlToElement = html => {
    const placeholder = document.createElement('div');
    placeholder.innerHTML = html;
    return placeholder.children.length ? placeholder.firstElementChild : undefined;
  };

  /* eslint-disable no-console */

  /**
   * Parse JSON in a safe way.
   */
  const parseJson = json => {
    try {
      return JSON.parse(json);
    } catch (e) {
      if (defined(console)) {
        console.warn(e);
      }
      return undefined;
    }
  };

  /**
   * Call preventDefault on an event object.
   * Might be combined with `tap` to create a chainable utility.
   * See also `preventingDefault`.
   */
  var preventDefault = e => e.preventDefault();

  /**
   * For doing side-effects.
   * The generated function returns whatever argument it receives, and executes the side-effect
   * function in the middle. Handy for chaining logs. For instance:
   *
   * myPromise().then(tap(x => console.log(x))).then(x => ...);
   */
  var tap = f =>
    x => {
      f(x);
      return x;
    };

  /**
   * Function decorator to create event listeners from common functions.
   * Avoids having to specify e.preventDefault() in functions, thus allowing the author to use them
   * outside an event listener context.
   */
  var preventingDefault = f => compose(f, tap(preventDefault));

  /**
   * Register event listeners and dispatch events.
   */
  const EventDispatcher = () => {

    const listeners = {};
    const queue = {};

    /**
     * Dispatch events to a certain listener type, or store them in a queue.
     */
    const dispatch = (type, payload) => {
      if (listeners[type]) {
        listeners[type].forEach(fn => fn(payload));
      } else {
        queue[type] = [...queue[type] || [], payload];
      }
    };

    /**
     * Store listener with callback function per type. Invoke the queue if it exists.
     */
    const add = (type, fn) => {
      listeners[type] = [...listeners[type] || [], fn];
      if (queue[type]) {
        queue[type].forEach(payload => dispatch(type, payload));
        delete queue[type];
      }
    };

    return {
      add,
      dispatch,
      listeners,
    };

  };

  /**
   * Dialog tab list with cookie tabs.
   */
  const DialogTabList = ({ config, preferences }) => {

    const events = EventDispatcher();

    const TYPE = config.get('type');
    const PREFIX = config.get('prefix');

    /**
     * Render cookie tabs.
     */
    const renderTab = ({ id, label, description, required, checked, accepted }, index) => {

      /**
       * Check if the checkbox should be checked:
       *
       * 1. If any preference is saved, use that.
       * 2. If it's explicitly `required` in the settings, mark it checked (ignore
       *    `required: false`, because of #3)
       * 3. Use the `checked` setting.
       */
      const shouldBeChecked = typeof accepted !== 'undefined'
        ? accepted
        : required === true
          ? required
          : checked;

      return `
      <li role="presentation">
        <header class="${PREFIX}__tab">
          <label class="${PREFIX}__option" data-required="${required}">
            <input type="${TYPE === 'radio' ? 'radio' : 'checkbox'}" name="${PREFIX}-input" value="${id}" ${shouldBeChecked ? 'checked' : ''} ${required && TYPE !== 'radio' ? 'disabled' : ''}>
            <span>${label}</span>
          </label>
          <a
            class="${PREFIX}__tab-toggle"
            role="tab"
            id="${PREFIX}-tab-${index}"
            href="#${PREFIX}-tabpanel-${index}"
            aria-controls="${PREFIX}-tabpanel-${index}"
            aria-selected="false"
            aria-label="${config.get('labels.aria.tabToggle')}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 16"><path d="M21.5.5l3 3.057-12 11.943L.5 3.557 3.5.5l9 9z"/></svg>
          </a>
        </header>
        <div
          class="${PREFIX}__tab-panel"
          role="tabpanel"
          id="${PREFIX}-tabpanel-${index}"
          aria-labelledby="${PREFIX}-tab-${index}"
          aria-hidden="true">
          <div class="${PREFIX}__tab-description">
            ${description}
          </div>
        </div>
      </li>
    `;
    };

    /**
     * Render cookie tab list.
     */
    const renderTabList = () => {
      const cookies = config.get('cookies', true) || [];
      const cookiesWithState = cookies.map(cookieType => ({
        ...cookieType,
        accepted: preferences.get(cookieType.id)
          ? preferences.get(cookieType.id).accepted
          : undefined,
      }));
      return `
      <ul class="${PREFIX}__tab-list" role="tablist" aria-label="${config.get('labels.aria.tabList')}">
        ${cookiesWithState.map(renderTab).join('')}
      </ul>
    `;
    };

    /**
     * Tab list element.
     */
    const tabList = htmlToElement(renderTabList());

    /**
     * Simple method to get values from cookie checkboxes.
     */
    const getValues = () => {
      const inputs = [...tabList.querySelectorAll('input')];
      return inputs.map(input => ({
        id: input.value,
        accepted: input.checked,
      }));
    };

    /**
     * Handle tab selection.
     */
    const selectTab = ({ tabs, panels, targetTab }) => {
      const controls = targetTab ? targetTab.getAttribute('aria-controls') : '';
      tabs.forEach(tab => tab.setAttribute('aria-selected', tab === targetTab));
      panels.forEach(panel => panel.setAttribute('aria-hidden', controls !== panel.id));
    };

    /**
     * Attach event listener for tab click event.
     */
    const attachTabClickListeners = () => {
      const tabs = [...tabList.querySelectorAll('[role="tab"]')];
      const panels = [...tabList.querySelectorAll('[role="tabpanel"]')];
      tabs.forEach(tab => {
        tab.addEventListener('click', e => {
          e.preventDefault();
          const targetTab = tab.getAttribute('aria-selected') === 'true' ? null : tab;
          selectTab({ tabs, panels, targetTab });
        });
      });
    };

    return {
      init() {
        attachTabClickListeners();
      },
      on: events.add,
      element: tabList,
      getValues,
    };

  };

  /**
   * Dialog which is shown to update cookie preferences.
   */
  const Dialog = ({ config, preferences }) => {

    const tabList = DialogTabList({ config, preferences });
    const events = EventDispatcher();

    const TYPE = config.get('type');
    const PREFIX = config.get('prefix');

    // Only allow the `acceptAllButton` when no preferences have been stored.
    const ACCEPT_ALL_BUTTON = config.get('acceptAllButton') && !preferences.hasPreferences();

    /**
     * Render dialog element.
     */
    const renderDialog = () => {
      return `
      <aside id="${PREFIX}" class="${PREFIX} js-cookie-bar" role="dialog" aria-live="polite" aria-describedby="${PREFIX}-description" aria-hidden="true" tabindex="0">
        <!--googleoff: all-->
        <header class="${PREFIX}__header" id="${PREFIX}-description">
          <h1>${config.get('labels.title')}</h1>
          ${config.get('labels.description')}
        </header>
        <form>
          <button class="${PREFIX}__button" aria-label="${config.get('labels.aria.button')}">
            <span>${config.get('labels.button.default')}</span>
          </button>
        </form>
        <!--googleon: all-->
      </aside>
    `;
    };

    /**
     * Dialog and form elements.
     */
    const dialog = htmlToElement(renderDialog());
    const form = dialog.querySelector('form');
    const button = form.querySelector('button');

    /**
     * Hide/show helpers.
     */
    const hide = () => dialog.setAttribute('aria-hidden', 'true');
    const show = () => dialog.setAttribute('aria-hidden', 'false');

    /**
     * Compose values based on input type, the `acceptAllButton` configuration,
     * any stored preferences and the state of the input options.
     *
     * When an option is checked, the form will just submit. When nothing is checked,
     * it depends on the type and the `acceptAllButton` config setting.
     *
     * - Radio + option checked: success
     * - Radio + nothing checked + has `acceptAllButton`: select first + success
     * - Radio + nothing checked + no `acceptAllButton`: fail
     *
     * - Checkbox + option(s) checked: success
     * - Checkbox + nothing checked + has `acceptAllButton`: select all + success
     * - Checkbox + nothing checked + no `acceptAllButton`: success
     */
    const composeValues = values => {

      // Radio when no option is selected.
      if (TYPE === 'radio' && !values.find(v => v.accepted)) {
        // If the `acceptAllButton` option is configured, select the first option and
        // let the form submit as if the user had selected it.
        if (ACCEPT_ALL_BUTTON) {
          values[0].accepted = true;
          return values;
        }
        // Do not submit if no option is selected and `acceptAllButton` is not configured.
        return [];
      }

      // Checkbox with `acceptAllButton` and no user-choosable option is checked.
      // We compare amount of required options against checked options.
      const requiredCount = config.get('cookies').filter(c => c.required).length;
      const checkedCount = values.filter(v => v.accepted).length;
      const userOptionsChecked = checkedCount >= requiredCount;
      if (ACCEPT_ALL_BUTTON && TYPE === 'checkbox' && !userOptionsChecked) {
        return values.map(value => ({
          ...value,
          accepted: true,
        }));
      }

      // Return the values untouched. Happens for:
      // - Radio when an option has been selected.
      // - Checkbox with or without checked option, except the `acceptAllButton` case above.
      return values;

    };

    /**
     * Handle form submits.
     */
    const submitHandler = e => {
      e.preventDefault();

      // Get values based on the rules defined in `composeValues`.
      const values = composeValues(tabList.getValues());
      if (!values) {
        return;
      }

      // Dispatch values and hide the dialog.
      events.dispatch('submit', values);
      hide();
    };

    /**
     * Update button label.
     */
    const updateButtonLabel = label => {
      button.textContent = label;
    };

    return {
      init() {
        // Initialize tab list and append it to the form.
        tabList.init();
        form.insertBefore(tabList.element, form.firstElementChild);

        // Attach submit listener to the form.
        form.addEventListener('submit', preventingDefault(submitHandler));

        // Update initial button label if `acceptAllButton` is set to true,
        // and no cookie preferences have been stored. Also listen for changes
        // in the form, and revert the button back to normal when an option has
        // been selected.
        if (ACCEPT_ALL_BUTTON) {
          updateButtonLabel(config.get('labels.button.acceptAll'));
          form.addEventListener('change', e => {
            updateButtonLabel(config.get('labels.button.default'));
          });
        }
      },
      on: events.add,
      show,
      hide,
      element: dialog,
    };

  };

  const GENERAL_ATTRIBUTE = 'data-cookie-consent';
  const ACCEPTED_STATE_ATTRIBUTE = 'data-cookie-consent-accepted';
  const REJECTED_STATE_ATTRIBUTE = 'data-cookie-consent-rejected';

  /**
   * DOM toggler, which enables conditional script tags or embedded content.
   */
  const DomToggler = config => {

    /**
     * Append a single script.
     * @TODO append it to the same location and re-add classes and attributes.
     */
    const appendScript = script => {
      const newScript = document.createElement('script');
      if (script.src) {
        newScript.src = script.src;
      } else {
        newScript.innerHTML = script.innerHTML;
      }
      document.head.appendChild(newScript);
    };

    /**
     * Enable script tags.
     * Note: scripts are not removed for the current view when preferences are updated.
     */
    const toggleScripts = ({ id, accepted }) => {
      if (!accepted) {
        return;
      }
      const scripts = document.documentElement.querySelectorAll(`script[${GENERAL_ATTRIBUTE}="${id}"]`);
      [...scripts].forEach(script => {
        appendScript(script);
        script.parentNode.removeChild(script);
      });
    };

    /**
     * Show a single iframe.
     */
    const showIframe = iframe => {
      const src = iframe.getAttribute('data-src');
      if (src) {
        iframe.setAttribute('src', src);
        iframe.removeAttribute('data-src');
      }
    };

    /**
     * Hide a single iframe.
     */
    const hideIframe = iframe => {
      const src = iframe.getAttribute('src');
      if (src) {
        iframe.setAttribute('data-src', src);
        iframe.removeAttribute('src');
      }
    };

    /**
     * Show conditional iframes based on wanted state.
     */
    const toggleConditionalIframes = ({ id, accepted }) => {
      const iframes = document.body.querySelectorAll(`iframe[${GENERAL_ATTRIBUTE}="${id}"]`);
      [...iframes].forEach(el => accepted ? showIframe(el) : hideIframe(el));
    };

    /**
     * Show a single element.
     */
    const showElement = el => {
      el.style.display = '';
      el.removeAttribute('aria-hidden');
      el.removeAttribute('hidden');
    };

    /**
     * Hide a single element.
     */
    const hideElement = el => {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
    };

    /**
     * Show conditional elements based on wanted state.
     */
    const toggleConditionalElements = ({ id, accepted }) => {
      const accepts = document.body.querySelectorAll(`[${ACCEPTED_STATE_ATTRIBUTE}="${id}"]`);
      const rejects = document.body.querySelectorAll(`[${REJECTED_STATE_ATTRIBUTE}="${id}"]`);
      [...accepts].forEach(el => accepted ? showElement(el) : hideElement(el));
      [...rejects].forEach(el => accepted ? hideElement(el) : showElement(el));
    };

    return {
      toggle: preferences => {
        const cookies = config.get('cookies') || [];
        cookies.forEach(type => {
          const accepted = preferences.getState(type.id);
          toggleScripts({ id: type.id, accepted });
          toggleConditionalElements({ id: type.id, accepted });
          toggleConditionalIframes({ id: type.id, accepted });
        });
      },
    };

  };

  /**
   * Store items in LocalStorage or Cookies.
   */
  const Storage = () => {

    var storageModule = window.localStorage;
    if(!supportsLocalStorage()) {
      storageModule = {
        setItem(key, val) {
          Cookies.set(key, val, { expires: 365 });
        },
        getItem(key) {
          return Cookies.get(key);
        },
      };
    }

    const get = (key, value) => storageModule.getItem(key, value);
    const set = (key, value) => storageModule.setItem(key, value);
    const has = key => storageModule.getItem(key) !== null;

    return {
      get,
      set,
      has,
    };

  };

  const KEY_SUFFIX = 'preferences';

  /**
   * Normalize cookie preferences and set/get from Storage.
   */
  const Preferences = prefix => {

    const KEY = `${prefix}-${KEY_SUFFIX}`;

    const storage = Storage();

    const getAll = () => parseJson(storage.get(KEY)) || [];
    const get = id => getAll().find(type => type.id === id);
    const getState = id => get(id) && get(id).accepted;
    const hasPreferences = () => storage.has(KEY) && getAll().length;
    const store = (cookies = []) => storage.set(KEY, JSON.stringify(cookies));

    return {
      get,
      getAll,
      getState,
      hasPreferences,
      store,
    };

  };

  /**
   * Main constructor, which provides the API to the outside.
   */
  const CookieConsent = settings => {

    // Show warning when settings are missing.
    if (typeof settings !== 'object' || !Object.keys(settings).length) {
      console.warn(`No settings specified.`);
    }

    // Construct 'classes'.
    const config = Config(settings);
    const preferences = Preferences(config.get('prefix'));
    const dialog = Dialog({ config, preferences });
    const domToggler = DomToggler(config);
    const events = EventDispatcher();

    // Update initial content.
    domToggler.toggle(preferences);

    const updatePreference = (cookies) => {
      preferences.store(cookies);
      events.dispatch('update', preferences.getAll());
      domToggler.toggle(preferences);
    };

    // Initialize dialog and bind `submit` event.
    dialog.init();
    dialog.on('submit', updatePreference);

    // Append dialog to the DOM, if this is not explicitly prevented.
    if (config.get('append') !== false) {
      const appendEl = document.querySelector('main') || document.body.firstElementChild;
      const container = appendEl ? appendEl.parentNode : document.body;
      container.insertBefore(dialog.element, appendEl);
    }

    // Show the dialog when no preferences are found. If found, fire the `update` event.
    if (preferences.hasPreferences()) {
      //events.dispatch('update', preferences.getAll());
    } else {
      // Show the dialog. Invoked via a timeout, to ensure it's added in the next cycle
      // to cater for possible transitions.
      window.setTimeout(() => dialog.show(), config.get('appendDelay'));
    }

    return {
      getDialog: () => dialog.element,
      hideDialog: dialog.hide,
      showDialog: dialog.show,
      isAccepted: preferences.getState,
      getPreferences: preferences.getAll,
      on: events.add,
      updatePreference,
    };

  };

  return CookieConsent;

}());
